import { useContext, useEffect } from 'react';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { TextInput, Select, CheckInput } from '@octano/global-ui';
import { useValidations } from '../../../../../hooks/useValidations';

import { CourseFormContext } from '../../providers/CourseProvider';
import { SectionTitle } from '../../../../../components/text';
import { CourseFormFields } from '../../types';
import { TranslationsKeys } from '../../../../../locales/translations';

type CourseDetailsSectionProps = {
  readOnly?: boolean;
};

/**
 * HU 15624 — Sección "Detalles principales" del tab Administrativo.
 *
 * Agrupa los campos OBLIGATORIOS de la asignatura (FRD §1.3.a):
 *   - Datos básicos: sigla, código, nombre, escuela, modalidad, nivel, créditos.
 *   - Subsección "Configuración adicional": Educación Continua, Habilitación,
 *     Supletorios.
 *
 * Los campos de horas (totalHours / presentialPedagogicalHours / weeklyHours)
 * se trasladaron a `CourseOptionalDetailsSection` (sección "Detalles
 * opcionales").
 */
const CourseDetailsSection = ({
  readOnly = false,
}: CourseDetailsSectionProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { msgValidations, rulesPatternNumeric } = useValidations();

  const { control, setValue, clearErrors } = useFormContext<CourseFormFields>();
  // Se usa `useWatch` (no `watch()`) a propósito: el método `watch()` no
  // re-renderiza de forma fiable al hidratar el formulario con `reset()` para
  // este campo, por lo que la validación y el estado disabled de créditos
  // quedaban desfasados. `useController` (CheckInput) y `useWatch` comparten la
  // suscripción reactiva y sí reflejan el valor cargado.
  const isCreditsNotApplicable = useWatch({
    control,
    name: 'isCreditsNotApplicable',
    defaultValue: false,
  });

  const { isDisabledToUpdate, formData } = useContext(CourseFormContext);

  // El check bloquea el input de créditos y lo fuerza a "0". Se mantiene
  // sincronizado vía efecto para cubrir el caso de marcar el check (incluido
  // el hidrato inicial desde una asignatura con créditos <= 0).
  useEffect(() => {
    if (isCreditsNotApplicable) {
      setValue('credits', '0', {
        shouldDirty: true,
        shouldValidate: false,
      });
      clearErrors('credits');
    }
  }, [isCreditsNotApplicable, setValue, clearErrors]);

  const {
    schools = [],
    academicLevels = [],
    modalities = [],
    replaceCourseGradeOptions = [],
    coursesFeatures,
  } = formData;

  const hasAdditionalConfig =
    !!coursesFeatures?.replaceCourseGradeAllowed ||
    !!coursesFeatures?.replaceEvaluationGradeAllowed;

  return (
    <>
      <Row className="pt-4">
        <Col xs={6} lg={4}>
          <TextInput
            name="shortening"
            label={t(`shortening`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={6} lg={4}>
          <TextInput
            name="code"
            label={t(`code`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <TextInput
            name="name"
            label={t(`name`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="school"
            label={t(`school`)}
            options={schools}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="modality"
            label={t(`modality`)}
            options={modalities}
            control={control}
            isClearable={true}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="academicLevel"
            label={t(`academicLevel`)}
            options={academicLevels}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <TextInput
            name="credits"
            label={t(`credits`)}
            control={control}
            rules={{
              pattern: rulesPatternNumeric,
              validate: (value) => {
                if (isCreditsNotApplicable) {
                  return true;
                }
                if (!value) {
                  return msgValidations.required;
                }
                return +value > 0 || msgValidations.invalidMin(1);
              },
            }}
            disabled={readOnly || isDisabledToUpdate || isCreditsNotApplicable}
          />
          <div className="mt-2 no-credits-check">
            {/*
              Debe ir enlazado vía `control` (no `value`/`onChange` manual): el
              CheckInput de global-ui sólo registra el campo en RHF —y por tanto
              refleja `reset()` al editar/ver— cuando recibe `control`. Sin él,
              `checked` se deriva de `props.value` y al recargar una asignatura
              con créditos <= 0 el check aparecía SIEMPRE desmarcado.
              El forzado de `credits` a "0" al marcar lo cubre el useEffect de
              arriba (depende de `isCreditsNotApplicable`).
              `disabled` mantiene el check no interactivo en modo ver/asignatura
              en uso; el override `.no-credits-check` (en _forms.scss) lo pinta
              en color primario aunque esté disabled, para que se lea marcado.
            */}
            <CheckInput
              name="isCreditsNotApplicable"
              control={control}
              label={t('isCreditsNotApplicable', 'No aplica créditos')}
              disabled={readOnly || isDisabledToUpdate}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="pt-4 pb-3">
          <SectionTitle text={t(`additionalConfigSubsection`)} />
        </Col>
        {hasAdditionalConfig && !!coursesFeatures?.replaceCourseGradeAllowed && (
          <Col xs={12} lg={4}>
            <Select
              name="replaceCourseGradeAllowed"
              label={t(`replaceCourseGradeAllowed`)}
              options={replaceCourseGradeOptions}
              control={control}
              disabled={readOnly}
            />
          </Col>
        )}
        {hasAdditionalConfig &&
          !!coursesFeatures?.replaceEvaluationGradeAllowed && (
            <Col xs={12} lg={4}>
              <Select
                name="replaceEvaluationGradeAllowed"
                label={t(`replaceEvaluationGradeAllowed`)}
                control={control}
                options={replaceCourseGradeOptions}
                disabled={readOnly}
              />
            </Col>
          )}
        <Col xs={12} lg={4}>
          <div id="isNonStructuredAvailableContainer">
            <Select
              name="isNonStructuredAvailable"
              label={t(
                'isNonStructuredAvailable',
                'Disponibilidad para educación continua',
              )}
              control={control}
              options={[
                { label: 'Si', value: 1 },
                { label: 'No', value: 0 },
              ]}
              rules={{ required: msgValidations.required }}
              disabled={readOnly}
            />
          </div>
          <UncontrolledTooltip target="isNonStructuredAvailableContainer">
            {t(
              'isNonStructuredAvailableTooltip',
              'En caso de marcar "Si", la asignatura podra ofertarse como curso o programa de educación continua.',
            )}
          </UncontrolledTooltip>
        </Col>
      </Row>
    </>
  );
};

export default CourseDetailsSection;
